<template>
  <div id="management">
    <div class="position-relative">
      <b-button
        variant="primary"
        class="mb-3"
        size="sm"
        v-on:click="goBack()"
      >
        <feather-icon
          width="16px"
          height="16px"
          icon="ArrowLeftIcon"
        />
      </b-button>
    </div>
    <div class="row" v-if="fileOnFlow.detalle.ejecucion">
      <div class="col-12">
        <a :href="selectedFile.url" v-if="selectedFile" target="_blank">
          <h5 class="text-primary underline d-inline-block mr-2">
            * Visualizando documento seleccionado {{selectedFile.nombreCompleto}}
            <small>({{selectedFile.nombre}})</small> *
          </h5>
        </a>
        <div v-if="selectedFile" class="shadow bg-gray">
          <div v-if="fileType === 'pdf'" class="d-flex">
            <PdfViewport
              ref="pdfViewport"
              :default-page="currentStepPage"
              :s3url="selectedFile.url"
              :name="selectedFile.nombreCompleto"
              :signatures="signatures"
              :signature-width="signatureWidth"
              :signature-height="signatureHeight"
              @loaded="setSign"
              @signature-drag-end="onSignatureDragEnd"
              @signature-delete="closeSign"
            />
          </div>
          <div v-if="fileType === 'image'">
            <b-img :src="selectedFile.url" fluid :alt="selectedFile.nombreCompleto"/>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="my-3 d-flex justify-content-between">
          <b-button
            id="toggle-btn"
            v-b-modal="'modal-comments'"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            class="btn-icon shadow-lg "
          >
            comentarios
            <feather-icon icon="MessageCircleIcon" />
          </b-button>
          <Comments
            v-if="fileOnFlow.detalle && fileOnFlow.detalle.ejecucion"
            :comments="fileOnFlow.detalle.ejecucion.comentarios"
          />
          <b-button
            v-if="showSignButton &&
              (selectedSign.proveedor !== 'FirmadorMinisterioEconomia' && showSignButton)"
            ref="signPosition"
            variant="warning"
            class="btn-icon shadow-lg sign-file"
            @click="setSign"
          >
            estampa
            <feather-icon icon="FeatherIcon" />
          </b-button>
        </div>
        <b-card class="shadow border-0 pt-0">
          <div class="bg-white pb-2">
            <h5 class="card-title mb-0" v-if="fileOnFlow.detalle && fileOnFlow.detalle.ejecucion">
              Expediente # {{fileOnFlow.detalle.ejecucion.numero}} en el
              {{fileOnFlow.detalle.ejecucion.nombre}}
            </h5>
            <div class="text-primary">
              {{fileOnFlow.detalle.ejecucion.descripcion}}
            </div>
          </div>
          <b-card-body class="p-0">
            <div class="mt-2 shadow">
              <Steps
                v-if="fileOnFlow.detalle && fileOnFlow.detalle.pasos"
                :steps="fileOnFlow.detalle.pasos"
                :ejecucion="fileOnFlow.detalle.ejecucion"
              />
            </div>
            <div class="mt-5 container-actions" v-if="fileOnFlow.detalle">
              <ManageDocument
                v-if="!isSigner"
                :title="getApprovalRolTitle"
                action="approve"
                color="outline-success"
                :step-id="step"
              />
              <ManageDocument
                v-show="userSignConfiguration.userSigns.length"
                v-if="fileType === 'pdf' && showSignButton && !toProcessFile && canSign"
                title="Firmar Adjunto"
                action="sign-attachment"
                color="outline-success"
                data-cy="sign"
                :step-id="step"
                :attachment="selectedFile"
                :get-sign="getPosition"
              />
              <ManageDocument
                v-show="userSignConfiguration.userSigns.length"
                v-if="toProcessFile && isSigner && !isOffice"
                title="Firmar"
                action="sign"
                color="outline-success"
                :step-id="step"
                :get-sign="getPosition"
                @selectedSign="selectedSign = $event"
              />
              <ManageDocument
                v-show="userSignConfiguration.userSigns.length"
                v-if="toProcessFile && isOffice && !isFileEDP"
                :title="dynamicTitle"
                action="manage"
                color="outline-success"
                :process="isOffice"
                :step-id="step"
                :get-sign="getPosition"
              />
              <ManageDocument
                v-if="isFileEDP && toProcessFile && isOffice"
                title="Entregar fecha"
                action="manage-date"
                color="outline-success"
                :step-id="step"
                :get-sign="getPosition"
              />
              <ManageDocument
                title="Rechazar"
                action="reject"
                color="outline-danger"
                :step-id="step"
              />
              <ManageDocument
                title="Comentar"
                action="comment"
                color="outline-secondary"
                :step-id="step"
                modal="comment"
              />
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import Ripple from 'vue-ripple-directive';
import { VBTooltip } from 'bootstrap-vue';
import { uuid } from 'vue-uuid';

import ManageDocument from '@/febos/chile/approvals/components/trays/managementTray/ManageDocument';
import Steps from '@/febos/chile/approvals/components/trays/managementTray/informationFlow/Steps';
import StepTypes from '@/febos/chile/dnt/mixins/StepTypes';
import { rolTypesCode } from '@/febos/chile/approvals/components/trays/managementTray/constants';
import PdfViewport from '@/febos/global/pdf/components/PdfViewport';
import { SIGNATURE_TYPES } from '@/febos/global/pdf/config';
import ApprovalActionsState from '@/febos/chile/dnt/mixins/ApprovalActionsState';
import Comments from '@/febos/chile/approvals/components/trays/managementTray/informationFlow/Comments';

const SIGNATURE_WIDTH = 150;
const SIGNATURE_HEIGHT = 50;

export default {
  components: {
    ManageDocument,
    Steps,
    PdfViewport,
    Comments
  },
  data() {
    return {
      fileType: undefined,
      signatures: [],
      stepTypes: StepTypes,
      selectedFile: undefined,
      titleSign: 'Firmar',
      toProcessFile: false,
      showSignButton: false,
      selectedSign: {},
      signatureWidth: SIGNATURE_WIDTH,
      signatureHeight: SIGNATURE_HEIGHT
    };
  },
  watch: {
    loading(value) {
      if (!value) {
        this.$vs.loading.close('#management > .con-vs-loading');
        return;
      }

      this.$vs.loading({
        container: '#management',
        scale: 0.6
      });
    }
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  computed: {
    ...mapGetters('Dnts', [
      'participants'
    ]),
    ...mapGetters('Empresas', [
      'userSignConfiguration',
      'company'
    ]),
    ...mapGetters('Approvals', [
      'loading',
      'fileOnFlow',
      'fileOnFlowAttachments'
    ]),
    getApprovalRolTitle() {
      const { detalle = {} } = this.fileOnFlow;
      const { pasos = [], ejecucion } = detalle;
      const actualStep = pasos
        .filter((step) => (
          step.id === ejecucion.pasoActualId
        )) || [];
      if (actualStep[0].paso.aprobacionesTipoRolId === rolTypesCode.visador) {
        return 'Visar';
      }
      return 'Aprobar';
    },
    canSign() {
      return this.userSignConfiguration.userSigns.length;
    },
    isOffice() {
      const { detalle = {} } = this.fileOnFlow;
      const { pasos = [], ejecucion } = detalle;
      return pasos
        .some((step) => (
          step.id === ejecucion.pasoActualId
          && ((step.paso.aprobacionesTipoPasoId === this.stepTypes.OFFICE.toString())
            || (step.paso.aprobacionesTipoPasoId === this.stepTypes.OFFICER.toString()))
        ));
    },
    currentStep() {
      const {
        detalle: {
          pasos = [],
          ejecucion
        } = {}
      } = this.fileOnFlow;
      return pasos
        .find((step) => (
          step.id === ejecucion.pasoActualId
        ));
    },
    currentStepPage() {
      return this.toProcessFile ? this.currentStep.pagina || 1 : 1;
    },
    hasSignaturePosition() {
      return (
        this.toProcessFile
        && this.currentStep.x
        && this.currentStep.y
        && this.currentStep.pagina
      );
    },
    isSigner() {
      const { detalle = {} } = this.fileOnFlow;
      const { pasos = [], ejecucion } = detalle;
      return pasos
        .some((step) => (
          step.id === ejecucion.pasoActualId
          && step.paso.aprobacionesTipoRolId === rolTypesCode.firmante
        ));
    },
    step() {
      const { detalle = {} } = this.fileOnFlow;
      const { pasos = [], ejecucion } = detalle;
      return this.fileOnFlow.detalle && pasos.find((step) => step.id === ejecucion.pasoActualId);
    },
    canReturn() {
      const { detalle = {} } = this.fileOnFlow;
      const { pasos = [], ejecucion = {} } = detalle;
      const { reversible } = ejecucion;
      const actualStep = pasos.find((step) => step.id === ejecucion.pasoActualId);
      if (!actualStep) return false;
      const isTemporal = actualStep.esTemporal;
      return actualStep.numeroPaso.toString() !== '1' && reversible && !isTemporal;
    },
    isFileEDP() {
      const { detalle = {} } = this.fileOnFlow;
      const { ejecucion = {} } = detalle;
      return ejecucion.tipoReferencia === 'ARCHIVO' && ejecucion.tipoObjetoCategoria === 'EDP';
    },
    dynamicTitle() {
      let isNumeration = false;
      if (this.fileOnFlow.meta) {
        isNumeration = Object.keys(this.fileOnFlow.meta.oficinaAcciones).includes('foliar');
      } else {
        isNumeration = true;
      }
      let returnTitle = isNumeration ? 'Numerar' : 'Tramitar';
      if (this.isFileEDP) {
        returnTitle = 'Fechar';
      }
      return returnTitle;
    }
  },
  methods: {
    ...mapActions('Approvals', [
      'getApprovalFileDetails',
      'getAttachmentFiles'
    ]),
    ...mapActions('Dnts', [
      'getFileDnt'
    ]),
    ...mapActions('Management', [
      'fetchAllDocuments'
    ]),
    closeSign() {
      this.signatures = [];
    },
    goBack() {
      this.$router.back();
    },
    setSign() {
      if (!this.canSign) {
        return;
      }

      if (!this.isSigner && this.toProcessFile) {
        return;
      }
      const isExtraAction = this.dynamicTitle === 'Tramitar';
      if (this.hasSignaturePosition && !isExtraAction) {
        const {
          x,
          y,
          pagina: page
        } = this.currentStep;
        const signatureId = uuid.v4();
        this.signatures = [{
          signatureId,
          x,
          y,
          page,
          type: this.isOffice ? SIGNATURE_TYPES.OfficeSignature : SIGNATURE_TYPES.UserSignature,
          ...(this.isOffice && {
            meta: {
              processType: this.dynamicTitle.toLowerCase()
            }
          })
        }];
        return;
      }

      if (this.isOffice && this.toProcessFile) {
        if (this.dynamicTitle === 'Numerar' || this.dynamicTitle === 'Fechar') {
          const pageNumber = this.$refs.pdfViewport.getPageNumber();
          const pageWidth = this.$refs.pdfViewport.getPageWidth();
          const signatureId = uuid.v4();
          this.signatures = [{
            signatureId,
            x: pageWidth - 250,
            y: 20,
            page: pageNumber,
            type: SIGNATURE_TYPES.DraggableOfficeSignature,
            meta: {
              processType: this.dynamicTitle.toLowerCase()
            }
          }];
          return;
        }

        if (this.dynamicTitle === 'Tramitar') {
          const pageNumber = this.$refs.pdfViewport.getPageNumber();
          const pageWidth = this.$refs.pdfViewport.getPageWidth();
          const pageHeight = this.$refs.pdfViewport.getPageHeight();
          const signatureId = uuid.v4();
          this.signatures = [{
            signatureId,
            x: pageWidth - 250,
            y: pageHeight - 70,
            page: pageNumber,
            type: SIGNATURE_TYPES.DraggableOfficeSignature,
            meta: {
              processType: this.dynamicTitle.toLowerCase()
            }
          }];
          return;
        }
      }

      const pageNumber = this.$refs.pdfViewport.getPageNumber();
      const pageHeight = this.$refs.pdfViewport.getPageHeight();
      const signatureId = uuid.v4();
      this.signatures = [{
        signatureId,
        x: 20,
        y: pageHeight - 70,
        page: pageNumber,
        type: SIGNATURE_TYPES.DraggableUserSignature
      }];

      if (this.fileOnFlow.acciones?.length > 0) {
        this.fileOnFlow.acciones
          .filter(
            (action) => action.estado === ApprovalActionsState.CREATED
              && (action.pagina && action.pagina > 0)
          ).forEach((action) => {
            const signatureActionId = uuid.v4();
            this.signatures.push({
              signatureId: signatureActionId,
              x: action.x,
              y: action.y,
              page: action.pagina,
              type: SIGNATURE_TYPES.OfficeSignature,
              isAction: true,
              meta: {
                processType: action.accion.toLowerCase()
              }
            });
          });
      }
    },
    getPosition() {
      const [signature] = this.signatures.filter(
        (signatureItem) => !signatureItem.isAction
      );
      if (!signature) {
        return {};
      }

      const {
        x,
        y,
        page
      } = signature;

      return {
        x,
        y,
        page
      };
    },
    selectFile(file) {
      this.signatures = [];
      this.selectedFile = undefined;
      this.fileType = undefined;

      if (file && (file.url.toLowerCase().includes('png')
        || file.url.toLowerCase().includes('jpg')
        || file.url.toLowerCase().includes('jpeg'))
      ) {
        this.fileType = 'image';
        this.selectedFile = file;
        return;
      }
      if (file && file.url.toLowerCase().includes('pdf')) {
        this.fileType = 'pdf';
        this.selectedFile = file;

        this.showSignButton = ((file.esInicial && this.isSigner) || file.id);
        this.toProcessFile = file.esInicial;
        return;
      }

      this.$bvToast.toast(
        'Este tipo de archivo no puede ser visualizado',
        {
          title: '¡Oops!',
          variant: 'warning',
          pauseOnHover: true,
          solid: true,
          noAutoHide: true
        }
      );
      window.open(file.url, '_blank');
    },
    onSignatureDragEnd({ x, y }, signatureDraged) {
      const [signature] = this.signatures.filter(
        (signatureItem) => signatureItem.signatureId === signatureDraged.signatureId
      );
      this.signatures = [
        {
          ...signature,
          x,
          y
        }, ...this.signatures.filter(
          (signatureItem) => signatureItem.signatureId !== signatureDraged.signatureId
        )
      ];
    }
  },
  async created() {
    const { fileId } = this.$route.params;
    const { flowId } = this.$route.params;
    try {
      this.$bvToast.toast(
        'Estamos buscando la información necesaria, espera unos segundos',
        {
          title: '¡Hola!',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 4000,
        }
      );
      await Promise.all([
        this.getApprovalFileDetails({
          aprobacionId: fileId,
          ejecucionId: flowId,
          retornarPdf: 'no'
        }),
        this.getAttachmentFiles({
          aprobacionId: fileId,
          ejecucionId: flowId
        })
      ]);
      this.selectFile(this.fileOnFlowAttachments[0]);
      if (this.fileOnFlow.detalle.documentosBase[0].tipoObjetoId === 'EXP') {
        await this.fetchAllDocuments();
        await this.getFileDnt({
          febosId: this.fileOnFlow.detalle.documentosBase[0].objetoId,
          destinatarios: 'si',
          retornarDnt: 'si'
        });
      }
    } catch (error) {
      console.error(error);
    }
  }
};
</script>
<style>
.annotationLayer {
  position: absolute !important;
  border-bottom: 1px solid #c3c3c3;
}

.linkAnnotation {
  transform: unset !important;
  transform-origin: unset !important;
  left: unset !important;
  top: unset !important;
  right: 0 !important;
  bottom: 0 !important;
}

.selected {
  background-color: rgba(116, 74, 135, 0.49);
  border: 1px solid rgb(230 191 247);
}

.manage-comments {
  bottom: 60px;
}
.container-attachment {
  max-height: 55vh;
  overflow-y: scroll;
}
.container-actions {
  height: 350px;
  overflow-y: scroll;
}

.container-pdf {
  max-height: 85vh;
  overflow-y: auto;
}
.page {
  height: auto;
  margin: 0 auto;
}
</style>
